<template>
    <div class="muven-container">
        <div class="muven-router-name-container">
            <p class="muven-router-name">Detalhes do Produto</p>
        </div>

        <div class="muven-content">
            <Breadcrumb :items="breadcrumbList" class="mb-3"/>

            <ProductStoreChannelStatusToggleCmp 
                ref="productStoreChannelStatusToggle" />

            <div class="" v-if="!loading && !loadError">
                <button class="btn btn-outline-primary mr-3 mb-3"
                    @click="gotoModification(product)">
                    Editar
                </button>

                <button class="btn btn-outline-secondary mr-3 mb-3"
                    :disabled="forcingSync"
                    @click="forceChannelsSync(product)">
                    Forçar sincronização

                    <Loading v-show="forcingSync" />
                </button>

                <button class="btn btn-outline-secondary mr-3 mb-3"
                    v-show="showProductChannelStatusManagement"
                    :disabled="openingStatusToggleModal"
                    @click="openProductStoreChannelStatusToggle(product)">
                    Gerenciar envio por canal

                    <Loading v-show="openingStatusToggleModal" />
                </button>

                <a class="btn btn-outline-secondary mb-3" 
                    :href="getAuditUrl(productAuditId, EntityEnum.Product)" target="_blank">
                    Ir para auditoria
                    <fa icon="external-link-alt" class="muven-external-link ml-1" />
                </a>
            </div>

            <Loading page v-if="loading" />            

            <ResourceNotFound
                v-if="!loading && loadError"
                resource="produto"
                :identifier="$route.params.id"
                list-path="/produtos"
                list-label="Voltar para produtos"
                :kind="loadErrorKind"
                :error-message="loadErrorMessage"
                @retry="load" />

            <template v-if="!loadError">
                <ProductIntegrationDiagnosticCmp
                    variant="detalhe"
                    :loading="loading"
                    :product="product"
                    :backend-name="subscriber.getBackendName()"
                    :channel-list="channelList"
                    :is-backend-shop="subscriber.isBackendShop()"
                    :audit-url="getAuditUrl(productAuditId, EntityEnum.Product)" />

                <div class="muven-tip muven-tip-standalone tip-danger mb-3" v-show="!loading && product.hasStockBlocked()">
                    <fa icon="info" class="tip-icon" />
                    <p>Este produto tem alguns estoques bloqueados, portanto a quantidade em estoque pode ser enviada de forma zerada para seus canais.</p>
                </div>

                <div class="muven-tip muven-tip-standalone tip-danger mb-3" v-show="!loading && !product.hasAnyImage()">
                    <fa icon="info" class="tip-icon" />
                    <p>Este produto não tem nenhuma imagem vinculada.</p>
                </div>

                <div v-show="!loading">
                    <ProductSimpleCmp 
                        v-if="!product.hasChildren()"
                        :product="product" />

                    <ProductWithVariationsCmp 
                        v-if="product.hasChildren()"
                        :product="product" />
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import { ProductModel } from '@/product/models/ProductModel'
import productService from '@/product/services/ProductService'
import { EntityEnum } from '@c2b/muven-commons'
import GetAuditUrl from '../../utils/mixins/GetAuditUrl'
import ProductSimpleCmp from '@/product/components/ProductSimpleCmp'
import ProductWithVariationsCmp from '@/product/components/ProductWithVariationsCmp'
import ProductStoreChannelStatusToggleCmp from '../components/ProductStoreChannelStatusToggleCmp'
import ProductIntegrationDiagnosticCmp from '../components/ProductIntegrationDiagnosticCmp'
import channelService from '../../channel/services/ChannelService'
import ResourceLoadError from '../../utils/mixins/ResourceLoadError'

export default {
    name: 'ProductWrapperView',    
    data() {
       return {
            EntityEnum,
            loading: true,
            forcingSync: false,
            openingStatusToggleModal: false,
            product: new ProductModel(),
            channelList: [],
            breadcrumbList: [
                {
                    id: 0,
                    description: 'Produtos',
                    path: '/produtos',
                },
                {
                    id: 1,
                    description: `Detalhes de #${this.$route.params.id}`,
                    path: '',
                },
            ],
       } 
    },
    components: {
        ProductSimpleCmp,
        ProductWithVariationsCmp,
        ProductStoreChannelStatusToggleCmp,
        ProductIntegrationDiagnosticCmp,
    },
    mixins: [
        GetAuditUrl,
        ResourceLoadError,
    ],
    mounted() {
        this.load()
    },
    methods: {
        async load() {
            this.loading = true
            this.loadError = null

            try {
                let product = await productService.getProductById(this.$route.params.id)
                this.channelList = await channelService.getChannels()

                if (!!product.parent) {
                    product = await productService.getProductById(product.parent.id)
                }

                this.product = ProductModel.normalizeV1(product, this.subscriber)
            } catch(e) {
                this.loadError = e
            } finally {
                this.loading = false
            }
        },
        gotoModification(product) {
            this.$router.push(`/produtos/${product.id}/edicao`)
        },
        async forceChannelsSync(product) {
            this.forcingSync = true

            try {
                await productService.forceChannelsSync(product.id)
                this.$floatingAlert.info({
                    message: `Solicitação de sincronização do produto <b>${product.name}</b> registrado com sucesso.`,
                    timeVisible: 10000,
                })
            } catch (err) {
                this.$floatingAlert.error({
                    message: err.message || `Houve um problema no momento de forçar a sincronização do produto <b>${product.name}</b>. Tente novamente mais tarde.`  
                })
            }

            this.forcingSync = false
        },
        async openProductStoreChannelStatusToggle(product) {
            this.openingStatusToggleModal = true

            try {
                await this.$refs.productStoreChannelStatusToggle.openModal(product)
            } catch (err) {
                this.$floatingAlert.error({
                    message: err.message,
                })
            }

            this.openingStatusToggleModal = false
        },
    },
    computed: {
        ...mapState({
            subscriber: state => state.subscriber,
        }),
        productAuditId() {
            if (!(this.product instanceof ProductModel)) {
                return undefined
            }

            if (!this.subscriber.isBackendBimer()) {
                return this.product.id
            }

            if (this.subscriber.isBackendBimer() && this.product.hasMoreThanOneChildren()) {
                return this.product.id
            }

            return this.product.firstChildId
        },
        showProductChannelStatusManagement() {
            return !!this.channelList.find((c) => {
                return c.isTray() || c.isB2W() || c.isNuvemShop() || c.isWooCommerce()
            })
        },
    },
}
</script>
